import { create } from "zustand"

import type {
  CompanyProductAssociationSort,
  ComputedProductStatus,
  GetCompanyProductAssociationListQuery,
  PriorityStatus,
} from "@/graphql/codegen/graphql"
import { GetCompanyProductAssociationListDocument } from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

export type CompanyProductAssociationListItem = NonNullable<
  GetCompanyProductAssociationListQuery["companyProductAssociation"]["list"]["items"]
>[number]

export type CustomerProductAssociationsFilters = SearchFilters & {
  companyId: string
  priorityStatuses?: PriorityStatus[]
  computedProductStatuses?: ComputedProductStatus[]
  sort?: CompanyProductAssociationSort
  shipToLocationId?: string
}

const deduplicateAssociations = (
  newData: CompanyProductAssociationListItem[],
  existingData: CompanyProductAssociationListItem[]
): CompanyProductAssociationListItem[] => {
  const existingIds = new Set(
    existingData.map(
      (i) => `${i.companyProductAssociationId}-${i.companyProductAssociationLocationId ?? "no-location"}`
    )
  )
  return [
    ...existingData,
    ...newData.filter(
      (i) =>
        !existingIds.has(`${i.companyProductAssociationId}-${i.companyProductAssociationLocationId ?? "no-location"}`)
    ),
  ]
}

export const useCustomerProductAssociationsStore = create(
  createSearchStore<
    CompanyProductAssociationListItem,
    CustomerProductAssociationsFilters,
    GetCompanyProductAssociationListQuery
  >({
    documentNode: GetCompanyProductAssociationListDocument,
    deduplicateData: deduplicateAssociations,
    getQueryInput: (filters, offset, limit) => ({
      companyId: filters.companyId,
      limit,
      offset,
      filter: {
        ...(filters.query && { query: filters.query }),
        ...(filters.priorityStatuses?.length && { priorityStatus: filters.priorityStatuses }),
        ...(filters.computedProductStatuses?.length && { computedProductStatuses: filters.computedProductStatuses }),
        ...(filters.shipToLocationId && { shipToLocationIds: [filters.shipToLocationId] }),
      },
      ...(filters.sort && { sort: filters.sort }),
    }),
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: "CompanyProductAssociationLocationListResult",
      data: data.companyProductAssociation.list.items ?? [],
    }),
  })
)
